import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, KeyRound, X, Loader2, CheckCircle2 } from "lucide-react";
import { useLang } from "@/context/LangContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtErr(detail) {
  if (!detail) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

// Reusable email-OTP gate. Calls onVerified(token) once the code is confirmed.
export const OtpGate = ({ onVerified, onClose }) => {
  const { t } = useLang();
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const inputStyle = { background: "var(--bg)", border: "2px solid var(--border)", color: "var(--text)" };

  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  const send = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/submissions/request-otp`, { email: email.trim().toLowerCase() });
      toast.success("Code sent to your email");
      setStep("code");
    } catch (err) { toast.error(fmtErr(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  const verify = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/submissions/verify-otp`, { email: email.trim().toLowerCase(), code: code.trim() });
      onVerified(data.token);
    } catch (err) { toast.error(fmtErr(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center px-4" style={{ background: "rgba(0,0,0,0.5)" }} data-testid="otp-gate" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="w-full max-w-md rounded-3xl p-6 sk-scale-in" style={{ background: "var(--card)", border: "2px solid var(--border)" }}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-ui text-lg font-bold" style={{ color: "var(--text)" }}>{t("otp_verify_first")}</h3>
          <button onClick={onClose} data-testid="otp-gate-close" aria-label="Close" className="grid place-items-center w-10 h-10 rounded-full" style={{ background: "var(--bg)", color: "var(--text-muted)" }}><X size={18} /></button>
        </div>
        {step === "email" ? (
          <form onSubmit={send} className="space-y-3">
            <label className="block">
              <span className="font-ui text-sm font-bold flex items-center gap-2" style={{ color: "var(--text-2)" }}><Mail size={16} /> {t("submit_email_label")}</span>
              <input data-testid="otp-gate-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 w-full h-13 h-[52px] px-4 rounded-xl font-ui text-lg outline-none" style={inputStyle} />
            </label>
            <button data-testid="otp-gate-send" disabled={busy} type="submit" className="w-full h-13 h-[52px] rounded-xl font-ui font-bold flex items-center justify-center gap-2 disabled:opacity-60" style={{ background: "var(--accent)", color: "#fff" }}>
              {busy ? <Loader2 size={18} className="animate-spin" /> : <KeyRound size={18} />} {t("submit_send_code")}
            </button>
          </form>
        ) : (
          <form onSubmit={verify} className="space-y-3">
            <p className="font-ui text-sm" style={{ color: "var(--text-muted)" }}>{email}</p>
            <input data-testid="otp-gate-code" inputMode="numeric" maxLength={6} required value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} className="w-full h-16 px-4 rounded-xl font-ui text-3xl tracking-[0.5em] text-center outline-none" style={inputStyle} />
            <button data-testid="otp-gate-verify" disabled={busy || code.length < 4} type="submit" className="w-full h-[52px] rounded-xl font-ui font-bold flex items-center justify-center gap-2 disabled:opacity-60" style={{ background: "var(--accent)", color: "#fff" }}>
              {busy ? <Loader2 size={18} className="animate-spin" /> : <CheckCircle2 size={18} />} {t("submit_verify")}
            </button>
            <button type="button" onClick={send} className="w-full font-ui text-sm font-semibold" style={{ color: "var(--accent)" }}>{t("submit_resend")}</button>
          </form>
        )}
      </div>
    </div>
  );
};
